import { 
  MeetingSessionConfiguration,
  NScaleVideoUplinkBandwidthPolicy
} from "amazon-chime-sdk-js";
import { DeviceLabels } from "amazon-chime-sdk-component-library-react";
import { MeetingMode } from "../../types";
import meetingConfig from "../../meetingConfig";

interface JoinMeetingOptions {
  meetingId: number;
  joinInfo: any; // The JoinInfo from the backend API response
  region: string;
  enableSimulcast: boolean;
  priorityBasedPolicy?: any;
  keepLastFrameWhenPaused: boolean;
  isWebAudioEnabled: boolean;
  isEchoReductionEnabled: boolean;
  meetingManager: any; // Replace with proper type from SDK
  setJoinInfo: (info: any) => void;
  setRegion: (region: string) => void;
  setMeetingMode: (mode: MeetingMode) => void;
}

export const joinMeeting = async ({
  meetingId,
  joinInfo,
  region,
  enableSimulcast,
  priorityBasedPolicy,
  keepLastFrameWhenPaused,
  isWebAudioEnabled,
  isEchoReductionEnabled,
  meetingManager,
  setJoinInfo,
  setRegion,
  setMeetingMode,
}: JoinMeetingOptions): Promise<void> => {
  // Use the JoinInfo from the backend API response
  const { JoinInfo } = joinInfo;

  // Save the join info
  setJoinInfo(JoinInfo);

  // Create meeting session configuration
  const meetingSessionConfiguration = new MeetingSessionConfiguration(
    JoinInfo?.Meeting,
    JoinInfo?.Attendee
  );

  // Set meeting session metadata for logging if available
  if (
    meetingConfig.postLogger &&
    meetingSessionConfiguration.meetingId &&
    meetingSessionConfiguration.credentials &&
    meetingSessionConfiguration.credentials.attendeeId
  ) {
    const existingMetadata = meetingConfig.postLogger.metadata;
    meetingConfig.postLogger.metadata = {
      ...existingMetadata,
      meetingId: meetingSessionConfiguration.meetingId,
      attendeeId: meetingSessionConfiguration.credentials.attendeeId,
    };
  }

  // Update region
  setRegion(JoinInfo.Meeting.MediaRegion);

  // Configure meeting options based on app state
  meetingSessionConfiguration.enableSimulcastForUnifiedPlanChromiumBasedBrowsers = enableSimulcast;
  if (priorityBasedPolicy) {
    meetingSessionConfiguration.videoDownlinkBandwidthPolicy = priorityBasedPolicy;
  }
  meetingSessionConfiguration.keepLastFrameWhenPaused = keepLastFrameWhenPaused;

  // Configure video uplink bandwidth policy for better bandwidth management
  // Use NScaleVideoUplinkBandwidthPolicy which automatically scales down bandwidth
  // as the number of remote video tiles increases
  // This policy helps maintain good video quality by reducing bandwidth per stream
  // when more participants join the meeting
  const selfAttendeeId = JoinInfo?.Attendee?.AttendeeId || 'unknown';
  meetingSessionConfiguration.videoUplinkBandwidthPolicy = new NScaleVideoUplinkBandwidthPolicy(
    selfAttendeeId,
    true, // scaleResolution - enable resolution scaling
    meetingConfig.logger
  );

  // Create join options with device labels - use None to be most permissive
  const options = {
    deviceLabels: DeviceLabels.None, // Most permissive - allows joining without any devices
    enableWebAudio: isWebAudioEnabled,
    skipDeviceSelection: true, // We handle device selection ourselves
  };

  // Join the meeting
  await meetingManager.join(meetingSessionConfiguration, options);

  // Start the meeting
  await meetingManager.start();

  // Configure 480p video quality at 30fps - matching Zoom's specifications
  // This should be called after the meeting session is started
  if (meetingManager.meetingSession?.audioVideo) {
    // Set video input quality to 480p (640x480) at 30 fps - matching Zoom's free account specs
    // 480p provides good quality while being accessible to free accounts
    // 30 fps matches Zoom's maximum supported frame rate for smooth video
    meetingManager.meetingSession.audioVideo.chooseVideoInputQuality(640, 480, 30);
    
    // Set maximum video bandwidth to 1000 Kbps for 480p at 30fps
    // This bandwidth supports the higher frame rate while maintaining good quality
    meetingManager.meetingSession.audioVideo.setVideoMaxBandwidthKbps(1000);
    
    // Add observer for connection quality monitoring
    // This helps detect when to adjust video quality based on network conditions
    meetingManager.meetingSession.audioVideo.addObserver({
      connectionDidSuggestStopVideo: () => {
        console.warn('Network suggests stopping video to preserve audio quality');
        // The SDK will automatically handle video quality adjustments
        // but we can add custom logic here if needed
      },
      connectionDidBecomeGood: () => {
        console.log('Connection quality improved');
        // Connection has improved, video quality can be maintained
      },
      connectionDidBecomePoor: () => {
        console.warn('Connection quality degraded');
        // Connection is poor, consider reducing video quality further
      }
    });
  }

  // Set meeting mode
  setMeetingMode(MeetingMode.Attendee);
}; 